import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, CheckCircle, Sparkle,
} from "@phosphor-icons/react";
import { MaskReveal, FadeUp, Stagger, StaggerItem } from "@/components/Motion";
import { PillButton, Eyebrow, CTASection } from "@/components/Shared";
import { AUDIENCE_LIST } from "@/data/content";
import infographic from "@/assets/infographic.png";

const CHAPTERS = [
  {
    n: "01",
    who: "Students",
    problem: "One student, a dozen applications.",
    text: "Every property demands its own form, its own documents, its own login. OneLease gives students a single profile and a single application that works everywhere.",
  },
  {
    n: "02",
    who: "Parents",
    problem: "The same pay stubs, uploaded again and again.",
    text: "Parents shouldn't re-submit financials for every listing. OneLease centralizes verification and makes co-signing a single, secure tap.",
  },
  {
    n: "03",
    who: "Property Managers",
    problem: "Dozens of applicants, zero consistency.",
    text: "Manual verification and mismatched paperwork slow everything down. OneLease delivers standardized, pre-verified applicants and faster approvals.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <motion.div style={{ y: blobY }} className="absolute -top-24 -right-20 h-80 w-80 rounded-full bg-ol-blue/15 blur-3xl" />
        <motion.div style={{ y: blobY }} className="absolute top-40 -left-24 h-72 w-72 rounded-full bg-ol-deep/10 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">
          <div>
            <h1 className="mt-2 text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-ol-ink">
              <MaskReveal lines={["STUDENT", "HOUSING,"]} delay={0.15} />
              <MaskReveal lines={["SIMPLIFIED."]} delay={0.39} lineClass="text-ol-blue" />
            </h1>
            <div className="mt-7 max-w-xl">
              <p className="text-lg sm:text-xl text-ol-gray leading-relaxed">
                One application and one verification process for <span className="text-ol-ink font-semibold">every</span> property.
                OneLease replaces the repetitive, stressful leasing shuffle with a single secure profile
                for students, parents and property managers.
              </p>
            </div>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <PillButton to="/waitlist" testId="hero-waitlist-cta">Join the Waitlist</PillButton>
              <Link
                to="/student"
                data-testid="hero-learn-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-semibold text-ol-ink border border-gray-200 hover:border-ol-blue hover:text-ol-blue transition-colors duration-200"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ol-gray">
              {["One profile", "One application", "One verification"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle size={18} weight="fill" className="text-ol-blue" /> {t}
                </span>
              ))}
            </div>
          </div>

          <motion.div style={{ y: imgY, rotate: imgRotate }} className="relative">
            <div className="floaty relative mx-auto max-w-[420px]">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-ol-blue/10 blur-2xl" />
              <img
                src={infographic}
                alt="What is OneLease infographic"
                data-testid="hero-infographic"
                className="relative w-full rounded-[2rem] shadow-[0_40px_90px_-30px_rgba(37,99,235,0.5)]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-gray-100 bg-ol-ink py-5" data-testid="marquee">
        <Marquee speed={38} gradient={false} autoFill>
          {["One Application", "One Verification", "One Secure Profile", "Apply Once", "Approved Faster", "Every Property", "Zero Repetition"].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 text-2xl sm:text-3xl font-extrabold tracking-tight text-white/90">
              {t} <Sparkle size={22} weight="fill" className="text-ol-blue" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* AUDIENCE CARDS */}
      <section className="px-5 sm:px-8 py-20 sm:py-28" data-testid="audience-cards">
        <div className="mx-auto max-w-7xl">
          <FadeUp className="max-w-2xl">
            <Eyebrow>Built for everyone in the lease</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight text-ol-ink leading-[1.05]">
              One platform. Three points of view.
            </h2>
            <p className="mt-4 text-lg text-ol-gray leading-relaxed">
              Pick your role to see exactly how OneLease removes the friction, paperwork and guesswork.
            </p>
          </FadeUp>

          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {AUDIENCE_LIST.map((a) => {
              const Icon = a.icon;
              return (
                <StaggerItem key={a.key}>
                  <Link
                    to={a.to}
                    data-testid={`audience-card-${a.key}`}
                    className="group block h-full rounded-[2rem] bg-white p-2 soft-shadow-sm hover:-translate-y-1.5 transition-transform duration-300"
                  >
                    <div className="relative h-44 overflow-hidden rounded-[1.6rem]">
                      <img src={a.image} alt={a.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ol-ink/50 to-transparent" />
                      <div className="absolute left-4 top-4 grid place-items-center h-12 w-12 rounded-2xl bg-white/95 backdrop-blur text-ol-blue shadow">
                        <Icon size={26} weight="duotone" />
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold text-ol-ink">{a.label}</h3>
                        <span className="grid place-items-center h-9 w-9 rounded-full bg-ol-surface text-ol-blue transition-colors duration-200 group-hover:bg-ol-blue group-hover:text-white">
                          <ArrowRight size={18} weight="bold" />
                        </span>
                      </div>
                      <p className="mt-2 text-ol-gray leading-relaxed">{a.intro}</p>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 bg-ol-surface" data-testid="manifesto">
        <div className="mx-auto max-w-6xl">
          <FadeUp className="max-w-2xl">
            <Eyebrow>The problem we solve</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight text-ol-ink leading-[1.05]">
              Leasing is broken. We fixed it.
            </h2>
          </FadeUp>

          <div className="mt-12 space-y-4">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.06}>
                <div className="group grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-10 rounded-[2rem] bg-white p-7 sm:p-10 soft-shadow-sm">
                  <div className="flex sm:block items-center gap-4">
                    <span className="text-5xl sm:text-7xl font-extrabold text-ol-blue/20 leading-none">{c.n}</span>
                    <span className="text-sm font-bold uppercase tracking-[0.18em] text-ol-blue">{c.who}</span>
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-ol-ink leading-tight">{c.problem}</h3>
                    <p className="mt-3 text-lg text-ol-gray leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* MID CTA */}
      <CTASection
        testId="mid-cta"
        title="Reduce risk. Speed approvals. Simplify everything."
        subtitle="OneLease improves communication for every party in the lease. Join the waitlist and be first when we launch."
      />

      {/* BOTTOM CTA handled by CTASection variant below (bottom) */}
      <section className="px-5 sm:px-8 pb-24" data-testid="closing">
        <div className="mx-auto max-w-4xl text-center">
          <FadeUp>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-ol-ink leading-[1.05]">
              Your next lease should take minutes, not weeks.
            </h2>
            <div className="mt-8 flex justify-center">
              <PillButton to="/waitlist" testId="bottom-waitlist-cta">Join the Waitlist</PillButton>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
